import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Role } from "@kash/shared";

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  mustChangePassword: boolean;
}

function parseJwtExpiry(token: string): number | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const payload = JSON.parse(atob(base64));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: number | null;
  setSession: (user: AuthUser, accessToken: string, refreshToken?: string) => void;
  setAccessToken: (accessToken: string, refreshToken?: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  getTimeUntilExpiry: () => number | null;
  isExpiringSoon: (thresholdMs?: number) => boolean;
}

const DEFAULT_EXPIRY_THRESHOLD = 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      accessTokenExpiresAt: null,
      setSession: (user, accessToken, refreshToken) => {
        const expiresAt = parseJwtExpiry(accessToken);
        const resolvedRt = refreshToken ?? get().refreshToken;
        
        set({
          user,
          accessToken,
          refreshToken: resolvedRt,
          accessTokenExpiresAt: expiresAt,
        });

        // Sincronizar cookie kash_rt en el cliente para el Middleware de Next.js
        if (typeof window !== "undefined" && resolvedRt) {
          const expiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
          document.cookie = `kash_rt=${resolvedRt}; path=/; expires=${expiry}; SameSite=Lax; Secure`;
        }
      },
      setAccessToken: (accessToken, refreshToken) => {
        const expiresAt = parseJwtExpiry(accessToken);
        set((state) => {
          const resolvedRt = refreshToken ?? state.refreshToken;
          
          if (typeof window !== "undefined" && resolvedRt) {
            const expiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
            document.cookie = `kash_rt=${resolvedRt}; path=/; expires=${expiry}; SameSite=Lax; Secure`;
          }
          
          return {
            accessToken,
            accessTokenExpiresAt: expiresAt,
            refreshToken: resolvedRt,
          };
        });
      },
      logout: () => {
        if (typeof window !== "undefined") {
          document.cookie = "kash_rt=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure";
        }
        set({ user: null, accessToken: null, refreshToken: null, accessTokenExpiresAt: null });
      },
      isAuthenticated: () => !!get().user && !!get().accessToken,
      getTimeUntilExpiry: () => {
        const expiresAt = get().accessTokenExpiresAt;
        if (!expiresAt) return null;
        return expiresAt - Date.now();
      },
      isExpiringSoon: (thresholdMs = DEFAULT_EXPIRY_THRESHOLD) => {
        const timeLeft = get().getTimeUntilExpiry();
        if (timeLeft === null) return false;
        return timeLeft <= thresholdMs && timeLeft > 0;
      },
    }),
    {
      name: "kash-auth",
      partialize: (state) => ({ 
        user: state.user, 
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        accessTokenExpiresAt: state.accessTokenExpiresAt
      }),
    }
  )
);
